import React from 'react';
import { Box, Typography, List, ListItem, ListItemIcon, ListItemText } from '@mui/material';
import LocalShippingIcon from '@mui/icons-material/LocalShipping'; // Icon for delivery/logistics
import SecurityIcon from '@mui/icons-material/Security'; // Icon for safety/rules

/**
 * Reusable component to display rules, regulations, and service policies for delivery personnel.
 * * @param {string} title - The main title of the policy section (e.g., "Delivery Service Standards").
 * @param {Array<string>} rules - An array of strings, where each string is a single rule or policy point.
 * @param {string} type - Optional type for icon/style differentiation (e.g., 'safety', 'conduct').
 */
export default function DeliveryPersonPolicyBox({ 
  title = "Delivery Service Policies", 
  rules = [], 
  type = 'service' 
}) {
  if (!rules || rules.length === 0) {
    return null; 
  }

  // Define colors and icons based on type prop for visual distinction
  const styleMap = {
    service: {
      color: '#0070c0', // Standard Blue
      lightBg: 'rgba(0, 112, 192, 0.1)',
      Icon: LocalShippingIcon 
    },
    safety: {
      color: '#d32f2f', // Red/Error for safety warnings
      lightBg: 'rgba(211, 47, 47, 0.1)',
      Icon: SecurityIcon
    },
    conduct: {
      color: '#008000', // Green for conduct/behavior
      lightBg: 'rgba(0, 128, 0, 0.1)',
      Icon: SecurityIcon
    }
  };

  const selectedStyle = styleMap[type] || styleMap.service;
  const PolicyIconComponent = selectedStyle.Icon;

  return (
    <Box
      sx={{
        p: 3,
        mb: 4,
        border: `1px solid ${selectedStyle.color}`,
        borderRadius: 2,
        backgroundColor: selectedStyle.lightBg,
      }}
    >
      <Typography variant="h5" component="h2" sx={{ mb: 2, color: selectedStyle.color }}>
        <ListItemIcon sx={{ minWidth: 35, display: 'inline-flex', verticalAlign: 'middle', color: selectedStyle.color }}>
            <PolicyIconComponent />
        </ListItemIcon>
        **{title}**
      </Typography>
      
      <List disablePadding>
        {rules.map((rule, index) => (
          <ListItem key={index} disableGutters sx={{ py: 0.5 }}>
            <ListItemText primary={rule} sx={{ fontSize: '0.95rem' }} />
          </ListItem>
        ))}
      </List>
    </Box>
  );
}